import React, { useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  Flex,
  Box,
  Heading,
  Text,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Button,
  Input,
  IconButton,
  toast
} from '@sparrowengg/twigs-react';
import {
  CloseIcon,
  SearchIcon,
  CopyIcon
} from '@sparrowengg/twigs-react-icons';
import { EventTrace } from '../types';

const PAGE_SIZE_OPTIONS = ['10', '25', '50'];
const DEFAULT_PAGE_SIZE = 50;

const formatDate = (dateString: string) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
};

const StatusBadge: React.FC<{
  status: string;
  variant?: 'success' | 'error' | 'default';
}> = ({ status, variant = 'default' }) => {
  const colors = {
    success: { bg: '#E6F4F1', text: '#0F6B59', border: '#B3E5D9' },
    error: { bg: '#FEE5E5', text: '#B42318', border: '#F4CCCC' },
    default: { bg: '#F5F5F5', text: '#666666', border: '#E0E0E0' }
  };
  const color = colors[variant];
  return (
    <Box
      css={{
        display: 'inline-block',
        backgroundColor: color.bg,
        color: color.text,
        padding: '$4 $8',
        borderRadius: '$sm',
        border: `1px solid ${color.border}`,
        fontSize: '$sm',
        fontWeight: 500,
        whiteSpace: 'nowrap'
      }}
    >
      {status}
    </Box>
  );
};

interface ExecutionDetailsModalProps {
  trace: EventTrace;
  isOpen: boolean;
  onClose: () => void;
}

const ExecutionDetailsModal: React.FC<ExecutionDetailsModalProps> = ({
  trace,
  isOpen,
  onClose
}) => {
  const [pageNumber, setPageNumber] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [searchText, setSearchText] = useState('');

  const executionsList = useMemo(() => {
    return Object.values(trace.executions || {}).sort(
      (a, b) =>
        new Date(b.executedAt).getTime() - new Date(a.executedAt).getTime()
    );
  }, [trace]);

  const filteredExecutions = useMemo(() => {
    return executionsList.filter((exec) => {
      const searchLower = searchText.toLowerCase();
      return (
        exec.appName?.toLowerCase().includes(searchLower) ||
        exec.appId?.toString().includes(searchLower) ||
        exec.status?.toLowerCase().includes(searchLower)
      );
    });
  }, [executionsList, searchText]);

  const paginatedExecutions = useMemo(() => {
    const start = pageNumber * pageSize;
    const end = start + pageSize;
    return filteredExecutions.slice(start, end);
  }, [filteredExecutions, pageNumber, pageSize]);

  const totalPages = Math.ceil(filteredExecutions.length / pageSize);

  const getStatusVariant = (status: string) => {
    switch ((status || '').toUpperCase()) {
      case 'COMPLETED':
        return 'success';
      case 'FAILED':
        return 'error';
      case 'PENDING':
      case 'TRIGGERED':
        return 'default';
      default:
        return 'default';
    }
  };

  const handlePageSizeChange = (size: string) => {
    setPageSize(parseInt(size));
    setPageNumber(0);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        css={{
          width: 'min(90vw, 1200px)',
          maxHeight: '90vh',
          padding: '$24'
        }}
      >
        <Flex
          justifyContent="space-between"
          alignItems="flex-start"
          css={{ marginBottom: '$20' }}
        >
          <Box>
            <Heading size="h5" css={{ marginBottom: '$4', color: '#333333' }}>
              Event Execution Details
            </Heading>
            <Flex alignItems="center" gap="$6">
              <Text size="sm" css={{ color: '#666666' }}>
                Trace ID:{' '}
                <Text css={{ fontFamily: 'monospace', color: '#999999' }}>
                  {trace.traceId}
                </Text>
              </Text>
              <IconButton
                icon={<CopyIcon />}
                variant="ghost"
                size="sm"
                onClick={async (e) => {
                  e.stopPropagation();
                  try {
                    await navigator.clipboard.writeText(trace.traceId);
                    toast({
                      title: 'Copied',
                      description: 'Trace ID copied to clipboard',
                      variant: 'default'
                    });
                  } catch (error) {
                    console.error('Failed to copy:', error);
                  }
                }}
                css={{
                  color: '#999999',
                  marginTop: '$7',
                  '&:hover': {
                    color: '#4A9CA6'
                  }
                }}
              />
            </Flex>
          </Box>
          <IconButton
            icon={<CloseIcon />}
            variant="ghost"
            onClick={onClose}
            size="lg"
          />
        </Flex>

        {/* Summary Cards */}
        <Flex gap="$12" css={{ marginBottom: '$20', flexWrap: 'wrap' }}>
          <Box
            css={{
              flex: 1,
              minWidth: 200,
              backgroundColor: '#FAFAFA',
              borderRadius: '$lg',
              padding: '$16',
              border: '1px solid #E8E8E8',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Text
              size="sm"
              css={{
                color: '#666666',
                marginBottom: '$4',
                textAlign: 'center'
              }}
            >
              Event Type
            </Text>
            <Heading size="h6" css={{ color: '#333333', textAlign: 'center' }}>
              {trace.eventType}
            </Heading>
          </Box>

          <Box
            css={{
              flex: 1,
              minWidth: 200,
              backgroundColor: '#E6F4F1',
              borderRadius: '$lg',
              padding: '$16',
              border: '1px solid #B3E5D9',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Text
              size="sm"
              css={{
                color: '#0F6B59',
                marginBottom: '$4',
                textAlign: 'center'
              }}
            >
              Successful
            </Text>
            <Heading size="h6" css={{ color: '#0F6B59', textAlign: 'center' }}>
              {trace.successCount}
            </Heading>
          </Box>

          <Box
            css={{
              flex: 1,
              minWidth: 200,
              backgroundColor: '#FEE5E5',
              borderRadius: '$lg',
              padding: '$16',
              border: '1px solid #F4CCCC',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Text
              size="sm"
              css={{
                color: '#B42318',
                marginBottom: '$4',
                textAlign: 'center'
              }}
            >
              Failed
            </Text>
            <Heading size="h6" css={{ color: '#B42318', textAlign: 'center' }}>
              {trace.failedCount}
            </Heading>
          </Box>

          <Box
            css={{
              flex: 1,
              minWidth: 200,
              backgroundColor: '#FAFAFA',
              borderRadius: '$lg',
              padding: '$16',
              border: '1px solid #E8E8E8',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Text
              size="sm"
              css={{
                color: '#666666',
                marginBottom: '$4',
                textAlign: 'center'
              }}
            >
              Total
            </Text>
            <Heading size="h6" css={{ color: '#333333', textAlign: 'center' }}>
              {trace.totalCount}
            </Heading>
          </Box>
        </Flex>

        {/* Search and Filter */}
        <Box css={{ marginBottom: '$16' }}>
          <Input
            placeholder="Search by app name, app ID, or status"
            value={searchText}
            onChange={(e) => {
              setSearchText(e.currentTarget.value);
              setPageNumber(0);
            }}
            leftIcon={<SearchIcon />}
            css={{ width: '100%' }}
          />
        </Box>

        {/* Executions Table */}
        <Box
          css={{
            borderRadius: '$lg',
            border: '1px solid #E8E8E8',
            overflow: 'hidden',
            marginBottom: '$16',
            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
          }}
        >
          <Box css={{ overflowX: 'auto' }}>
            <Table css={{ width: '100%' }}>
              <Thead>
                <Tr css={{ backgroundColor: '#F5F5F5' }}>
                  <Th css={{ padding: '$12', textAlign: 'left' }}>
                    <Text weight="bold" size="sm" css={{ color: '#333333' }}>
                      App ID
                    </Text>
                  </Th>
                  <Th css={{ padding: '$12', textAlign: 'left' }}>
                    <Text weight="bold" size="sm" css={{ color: '#333333' }}>
                      App Name
                    </Text>
                  </Th>
                  <Th css={{ padding: '$12', textAlign: 'left' }}>
                    <Text weight="bold" size="sm" css={{ color: '#333333' }}>
                      Executed At
                    </Text>
                  </Th>
                  <Th css={{ padding: '$12', textAlign: 'center' }}>
                    <Text weight="bold" size="sm" css={{ color: '#333333' }}>
                      Status
                    </Text>
                  </Th>
                  <Th css={{ padding: '$12', textAlign: 'left' }}>
                    <Text weight="bold" size="sm" css={{ color: '#333333' }}>
                      Error Message
                    </Text>
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {paginatedExecutions.map((execution, index) => (
                  <Tr
                    key={`${execution.executionId}-${index}`}
                    css={{
                      borderBottom: '1px solid #E8E8E8',
                      '&:hover': {
                        backgroundColor: '#F9F9F9'
                      }
                    }}
                  >
                    <Td css={{ padding: '$12' }}>
                      <Text
                        size="sm"
                        weight="medium"
                        css={{ color: '#333333' }}
                      >
                        {execution.appId}
                      </Text>
                    </Td>
                    <Td css={{ padding: '$12' }}>
                      <Text size="sm" css={{ color: '#333333' }}>
                        {execution.appName}
                      </Text>
                    </Td>
                    <Td css={{ padding: '$12' }}>
                      <Text size="sm" css={{ color: '#666666' }}>
                        {formatDate(execution.executedAt)}
                      </Text>
                    </Td>
                    <Td css={{ padding: '$12', textAlign: 'center' }}>
                      <StatusBadge
                        status={execution.status}
                        variant={getStatusVariant(execution.status)}
                      />
                    </Td>
                    <Td css={{ padding: '$12' }}>
                      <Text
                        size="sm"
                        css={{
                          color: execution.errorMessage ? '#B42318' : '#999999',
                          fontFamily: 'monospace',
                          maxWidth: 300,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                        title={execution.errorMessage || ''}
                      >
                        {execution.errorMessage || '-'}
                      </Text>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>

          {/* Pagination */}
          <Flex
            justifyContent="space-between"
            alignItems="center"
            css={{
              padding: '$12 $16',
              borderTop: '1px solid #E8E8E8',
              backgroundColor: '#FAFAFA',
              flexWrap: 'wrap',
              gap: '$12'
            }}
          >
            <Text size="sm" css={{ color: '#666666' }}>
              {filteredExecutions.length > 0
                ? `${pageNumber * pageSize + 1} - ${Math.min(
                    (pageNumber + 1) * pageSize,
                    filteredExecutions.length
                  )} of ${filteredExecutions.length}`
                : 'No results'}
            </Text>

            <Flex gap="$4" alignItems="center">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPageNumber(Math.max(0, pageNumber - 1))}
                disabled={pageNumber === 0}
                css={{
                  minWidth: 80
                }}
              >
                Previous
              </Button>

              <Flex gap="$2">
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter((page) => {
                    const maxPagesToShow = 5;
                    if (totalPages <= maxPagesToShow) return true;
                    if (page === 1 || page === totalPages) return true;
                    if (Math.abs(page - (pageNumber + 1)) <= 1) return true;
                    return false;
                  })
                  .map((page, index, array) => {
                    if (index > 0 && array[index - 1] !== page - 1) {
                      return (
                        <Box key={`ellipsis-${page}`} css={{ px: '$2' }}>
                          <Text size="sm" css={{ color: '#999999' }}>
                            ...
                          </Text>
                        </Box>
                      );
                    }
                    return (
                      <Button
                        key={page}
                        variant={pageNumber + 1 === page ? 'solid' : 'outline'}
                        size="sm"
                        onClick={() => setPageNumber(page - 1)}
                        css={{
                          minWidth: 40,
                          backgroundColor:
                            pageNumber + 1 === page ? '#4A9CA6' : 'transparent',
                          color: pageNumber + 1 === page ? 'white' : '#666666',
                          border:
                            pageNumber + 1 === page
                              ? 'none'
                              : '1px solid #E0E0E0'
                        }}
                      >
                        {page}
                      </Button>
                    );
                  })}
              </Flex>

              <Button
                variant="outline"
                size="sm"
                onClick={() => setPageNumber(pageNumber + 1)}
                disabled={pageNumber >= totalPages - 1}
                css={{
                  minWidth: 80
                }}
              >
                Next
              </Button>
            </Flex>
          </Flex>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ExecutionDetailsModal;
